/**
 * Organizaciones: alta de clientes de la plataforma, estado, zona horaria,
 * uso y personas con acceso.
 *
 * Es una pantalla exclusiva del personal de la plataforma: la API solo responde
 * a `user.isSuperAdmin` y sus rutas no operan dentro de una organizacion, asi
 * que se llaman sin cabecera `X-Tenant-Id`.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type { TenantView } from '@shared/api-types.ts';
import { MEMBER_ROLE_LABELS, MEMBER_ROLES } from '@domain/types.ts';
import type { MemberRole } from '@domain/types.ts';
import { api, errorMessage, isAbortError } from '../api.ts';
import { formatDateTime } from '../format.ts';
import type { NoticeKind } from '../hooks.ts';
import { Chip, EmptyState, ErrorBanner, Field, SkeletonRows, Spinner } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

type State =
  | { status: 'loading' }
  | { status: 'ready'; tenants: TenantView[] }
  | { status: 'error'; message: string };

export function TenantsScreen({
  onNotice,
}: {
  onNotice: (kind: NoticeKind, text: string) => void;
}): ReactElement {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [timezones, setTimezones] = useState<string[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  // Alta de una organizacion nueva.
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [withOwner, setWithOwner] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Fila desplegada: datos de la organizacion y alta de personas.
  const [openId, setOpenId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editTimezone, setEditTimezone] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [memberEmail, setMemberEmail] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberPassword, setMemberPassword] = useState('');
  const [memberRole, setMemberRole] = useState<MemberRole>('agent');
  const [addingMember, setAddingMember] = useState(false);
  const [memberError, setMemberError] = useState<string | null>(null);

  // Suspension (se pide confirmacion porque corta el servicio al cliente).
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    api
      .listTenants(controller.signal)
      .then((tenants) => setState({ status: 'ready', tenants }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setState({ status: 'error', message: errorMessage(error) });
      });
    return () => controller.abort();
  }, [reloadToken]);

  // Las zonas horarias solo alimentan los desplegables: si fallan, la pantalla
  // sigue usable con las zonas ya conocidas y con UTC.
  useEffect(() => {
    const controller = new AbortController();
    api
      .listTenantTimezones(controller.signal)
      .then((values) => setTimezones(values))
      .catch(() => undefined);
    return () => controller.abort();
  }, [reloadToken]);

  const timezoneOptions = useMemo(() => {
    const options = [...timezones];
    const push = (value: string): void => {
      if (value && !options.includes(value)) options.push(value);
    };
    if (state.status === 'ready') {
      for (const tenant of state.tenants) push(tenant.timezone);
    }
    push('UTC');
    return options;
  }, [state, timezones]);

  const openRow = useCallback((tenant: TenantView): void => {
    setOpenId((previous) => (previous === tenant.id ? null : tenant.id));
    setEditName(tenant.name);
    setEditTimezone(tenant.timezone);
    setEditError(null);
    setMemberEmail('');
    setMemberName('');
    setMemberPassword('');
    setMemberRole('agent');
    setMemberError(null);
  }, []);

  const handleCreate = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      const cleanName = name.trim();
      if (cleanName.length < 2) {
        setFormError('El nombre debe tener al menos 2 caracteres.');
        return;
      }

      const owner = withOwner
        ? { email: ownerEmail.trim(), name: ownerName.trim(), password: ownerPassword }
        : null;
      if (owner) {
        if (!owner.email || !owner.name) {
          setFormError('El correo y el nombre del primer usuario son obligatorios.');
          return;
        }
        if (owner.password.length < 8) {
          setFormError('La contrasena del primer usuario debe tener al menos 8 caracteres.');
          return;
        }
      }

      setFormError(null);
      setCreating(true);
      api
        .createTenant({
          name: cleanName,
          ...(timezone ? { timezone } : {}),
          ...(owner ? { owner } : {}),
        })
        .then((response) => {
          setState((previous) =>
            previous.status === 'ready'
              ? { status: 'ready', tenants: [response.tenant, ...previous.tenants] }
              : previous,
          );
          setName('');
          setTimezone('');
          setWithOwner(false);
          setOwnerEmail('');
          setOwnerName('');
          setOwnerPassword('');
          onNotice(
            'success',
            response.ownerUserId
              ? 'Organizacion creada con su primer usuario.'
              : 'Organizacion creada.',
          );
        })
        .catch((error: unknown) => setFormError(errorMessage(error)))
        .finally(() => setCreating(false));
    },
    [name, onNotice, ownerEmail, ownerName, ownerPassword, timezone, withOwner],
  );

  const handleSaveEdit = useCallback(
    (tenant: TenantView): void => {
      const cleanName = editName.trim();
      const cleanTimezone = editTimezone.trim();
      if (cleanName.length < 2) {
        setEditError('El nombre debe tener al menos 2 caracteres.');
        return;
      }
      if (!cleanTimezone) {
        setEditError('La zona horaria es obligatoria.');
        return;
      }

      setEditError(null);
      setSavingEdit(true);
      api
        .updateTenant(tenant.id, { name: cleanName, timezone: cleanTimezone })
        .then((updated) => {
          setState((previous) =>
            previous.status === 'ready'
              ? {
                  status: 'ready',
                  tenants: previous.tenants.map((item) =>
                    item.id === updated.id ? updated : item,
                  ),
                }
              : previous,
          );
          onNotice('success', 'Organizacion actualizada.');
        })
        .catch((error: unknown) => setEditError(errorMessage(error)))
        .finally(() => setSavingEdit(false));
    },
    [editName, editTimezone, onNotice],
  );

  const handleToggleActive = useCallback(
    (tenant: TenantView): void => {
      const nextActive = !tenant.isActive;
      setToggling(tenant.id);
      api
        .updateTenant(tenant.id, { isActive: nextActive })
        .then((updated) => {
          setState((previous) =>
            previous.status === 'ready'
              ? {
                  status: 'ready',
                  tenants: previous.tenants.map((item) =>
                    item.id === updated.id ? updated : item,
                  ),
                }
              : previous,
          );
          setPendingToggle(null);
          onNotice(
            'success',
            nextActive ? 'Organizacion reactivada.' : 'Organizacion suspendida.',
          );
        })
        .catch((error: unknown) => onNotice('error', errorMessage(error)))
        .finally(() => setToggling(null));
    },
    [onNotice],
  );

  const handleAddMember = useCallback(
    (event: FormEvent<HTMLFormElement>, tenant: TenantView): void => {
      event.preventDefault();
      const email = memberEmail.trim();
      const cleanName = memberName.trim();
      if (!email || !cleanName) {
        setMemberError('El correo y el nombre son obligatorios.');
        return;
      }
      if (memberPassword.length < 8) {
        setMemberError('La contrasena debe tener al menos 8 caracteres.');
        return;
      }

      setMemberError(null);
      setAddingMember(true);
      api
        .addTenantMember(tenant.id, {
          email,
          name: cleanName,
          password: memberPassword,
          role: memberRole,
        })
        .then(() => {
          // El contador se actualiza en local; el proximo refresco trae el real.
          setState((previous) =>
            previous.status === 'ready'
              ? {
                  status: 'ready',
                  tenants: previous.tenants.map((item) =>
                    item.id === tenant.id
                      ? { ...item, memberCount: item.memberCount + 1 }
                      : item,
                  ),
                }
              : previous,
          );
          setMemberEmail('');
          setMemberName('');
          setMemberPassword('');
          setMemberRole('agent');
          onNotice('success', `Persona anadida a ${tenant.name}.`);
        })
        .catch((error: unknown) => setMemberError(errorMessage(error)))
        .finally(() => setAddingMember(false));
    },
    [memberEmail, memberName, memberPassword, memberRole, onNotice],
  );

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Organizaciones</h1>
          <p className="page__subtitle">
            Clientes de la plataforma: estado, zona horaria, uso y personas con acceso.
          </p>
        </div>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          <Icon name="refresh" />
          Actualizar
        </button>
      </header>

      <section className="card">
        <h2 className="card__title">Nueva organizacion</h2>

        <form onSubmit={handleCreate}>
          <div className="grid grid--2">
            <Field
              label="Nombre"
              htmlFor="tenant-name"
              required
              hint="Entre 2 y 120 caracteres; el identificador se genera solo."
            >
              <input
                id="tenant-name"
                className="input"
                maxLength={120}
                value={name}
                placeholder="Clinica Dental Sur"
                onChange={(event) => setName(event.target.value)}
                disabled={creating}
              />
            </Field>

            <Field
              label="Zona horaria"
              htmlFor="tenant-timezone"
              hint="Se usa para horarios de atencion y metricas."
            >
              <select
                id="tenant-timezone"
                className="select"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                disabled={creating}
              >
                <option value="">Predeterminada de la plataforma</option>
                {timezoneOptions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="card__row">
            <div>
              <h3 className="card__title">Primer usuario</h3>
              <p className="card__hint">
                Opcional. Si lo indicas, la organizacion nace con una persona propietaria ya
                dentro; si lo dejas desactivado, se crea vacia y podras anadir miembros despues.
              </p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={withOwner}
                onChange={(event) => setWithOwner(event.target.checked)}
                disabled={creating}
              />
              <span className="switch__track" aria-hidden="true">
                <span className="switch__thumb" />
              </span>
              <span className="switch__label">{withOwner ? 'Se crea' : 'Sin usuario'}</span>
            </label>
          </div>

          {withOwner ? (
            <div className="grid grid--3">
              <Field label="Correo" htmlFor="owner-email" required>
                <input
                  id="owner-email"
                  className="input"
                  type="email"
                  maxLength={200}
                  value={ownerEmail}
                  placeholder="responsable@cliente.com"
                  onChange={(event) => setOwnerEmail(event.target.value)}
                  disabled={creating}
                />
              </Field>

              <Field label="Nombre" htmlFor="owner-name" required>
                <input
                  id="owner-name"
                  className="input"
                  maxLength={120}
                  value={ownerName}
                  onChange={(event) => setOwnerName(event.target.value)}
                  disabled={creating}
                />
              </Field>

              <Field
                label="Contrasena"
                htmlFor="owner-password"
                required
                hint="Minimo 8 caracteres."
              >
                <input
                  id="owner-password"
                  className="input"
                  type="password"
                  maxLength={200}
                  value={ownerPassword}
                  onChange={(event) => setOwnerPassword(event.target.value)}
                  disabled={creating}
                />
              </Field>
            </div>
          ) : null}

          {formError ? (
            <div className="banner banner--error" role="alert">
              <Icon name="error" className="banner__icon" />
              <p className="banner__text">{formError}</p>
            </div>
          ) : null}

          <div className="page__actions page__actions--end">
            <button type="submit" className="button button--primary" disabled={creating}>
              {creating ? <Spinner label="Creando…" /> : (<><Icon name="plus" />Crear organizacion</>)}
            </button>
          </div>
        </form>
      </section>

      {state.status === 'loading' ? <SkeletonRows rows={4} /> : null}

      {state.status === 'error' ? (
        <ErrorBanner message={state.message} onRetry={() => setReloadToken((token) => token + 1)} />
      ) : null}

      {state.status === 'ready' && state.tenants.length === 0 ? (
        <EmptyState
          icon="tenants"
          title="Todavia no hay organizaciones"
          description="Da de alta al primer cliente para que su equipo pueda empezar a atender conversaciones."
        />
      ) : null}

      {state.status === 'ready' && state.tenants.length > 0 ? (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>Organizacion</th>
                <th>Estado</th>
                <th>Zona horaria</th>
                <th>Personas</th>
                <th>Cuentas</th>
                <th>Conversaciones activas</th>
                <th>Alta</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {state.tenants.map((tenant) => (
                <Fragment key={tenant.id}>
                  <tr>
                    <td className="table__strong">
                      {tenant.name}
                      <br />
                      <span className="table__mono">{tenant.slug}</span>
                    </td>
                    <td>
                      <Chip tone={tenant.isActive ? 'success' : 'danger'}>
                        {tenant.isActive ? 'Activa' : 'Suspendida'}
                      </Chip>
                    </td>
                    <td>{tenant.timezone}</td>
                    <td>{tenant.memberCount}</td>
                    <td>{tenant.accountCount}</td>
                    <td>{tenant.activeConversations}</td>
                    <td>{formatDateTime(tenant.createdAt)}</td>
                    <td>
                      <span className="page__actions">
                        <button
                          type="button"
                          className="button button--ghost button--sm"
                          onClick={() => openRow(tenant)}
                        >
                          <Icon name={openId === tenant.id ? 'close' : 'panel'} />
                          {openId === tenant.id ? 'Cerrar' : 'Gestionar'}
                        </button>

                        {tenant.isActive ? (
                          pendingToggle === tenant.id ? (
                            <>
                              <button
                                type="button"
                                className="button button--danger button--sm"
                                onClick={() => handleToggleActive(tenant)}
                                disabled={toggling === tenant.id}
                              >
                                {toggling === tenant.id ? (
                                  <Spinner label="Suspendiendo…" />
                                ) : (
                                  'Confirmar suspension'
                                )}
                              </button>
                              <button
                                type="button"
                                className="button button--ghost button--sm"
                                onClick={() => setPendingToggle(null)}
                                disabled={toggling === tenant.id}
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="button button--ghost button--sm"
                              onClick={() => setPendingToggle(tenant.id)}
                              title="Al suspenderla, su equipo y sus integraciones dejan de poder usar el panel hasta reactivarla"
                            >
                              Suspender
                            </button>
                          )
                        ) : (
                          <button
                            type="button"
                            className="button button--ghost button--sm"
                            onClick={() => handleToggleActive(tenant)}
                            disabled={toggling === tenant.id}
                          >
                            {toggling === tenant.id ? (
                              <Spinner label="Reactivando…" />
                            ) : (
                              <><Icon name="check" />Reactivar</>
                            )}
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>

                  {openId === tenant.id ? (
                    <tr>
                      <td className="table__detail" colSpan={8}>
                        <div className="grid grid--2">
                          <section className="card">
                            <h3 className="card__title">Datos de la organizacion</h3>

                            <form
                              onSubmit={(event) => {
                                event.preventDefault();
                                handleSaveEdit(tenant);
                              }}
                            >
                              <Field label="Nombre" htmlFor={`tenant-name-${tenant.id}`} required>
                                <input
                                  id={`tenant-name-${tenant.id}`}
                                  className="input"
                                  maxLength={120}
                                  value={editName}
                                  onChange={(event) => setEditName(event.target.value)}
                                  disabled={savingEdit}
                                />
                              </Field>

                              <Field
                                label="Zona horaria"
                                htmlFor={`tenant-tz-${tenant.id}`}
                                required
                              >
                                <select
                                  id={`tenant-tz-${tenant.id}`}
                                  className="select"
                                  value={editTimezone}
                                  onChange={(event) => setEditTimezone(event.target.value)}
                                  disabled={savingEdit}
                                >
                                  {timezoneOptions.map((value) => (
                                    <option key={value} value={value}>
                                      {value}
                                    </option>
                                  ))}
                                </select>
                              </Field>

                              {editError ? (
                                <div className="banner banner--error" role="alert">
                                  <Icon name="error" className="banner__icon" />
                                  <p className="banner__text">{editError}</p>
                                </div>
                              ) : null}

                              <div className="page__actions page__actions--end">
                                <button
                                  type="submit"
                                  className="button button--primary"
                                  disabled={savingEdit}
                                >
                                  {savingEdit ? (
                                    <Spinner label="Guardando…" />
                                  ) : (
                                    <><Icon name="check" />Guardar cambios</>
                                  )}
                                </button>
                              </div>
                            </form>
                          </section>

                          <section className="card">
                            <h3 className="card__title">Anadir persona</h3>
                            <p className="card__hint">
                              Si el correo ya existe se reutiliza la cuenta y solo se le anade
                              acceso a esta organizacion.
                            </p>

                            <form onSubmit={(event) => handleAddMember(event, tenant)}>
                              <Field
                                label="Correo"
                                htmlFor={`member-email-${tenant.id}`}
                                required
                              >
                                <input
                                  id={`member-email-${tenant.id}`}
                                  className="input"
                                  type="email"
                                  maxLength={200}
                                  value={memberEmail}
                                  onChange={(event) => setMemberEmail(event.target.value)}
                                  disabled={addingMember}
                                />
                              </Field>

                              <div className="grid grid--2">
                                <Field
                                  label="Nombre"
                                  htmlFor={`member-name-${tenant.id}`}
                                  required
                                >
                                  <input
                                    id={`member-name-${tenant.id}`}
                                    className="input"
                                    maxLength={120}
                                    value={memberName}
                                    onChange={(event) => setMemberName(event.target.value)}
                                    disabled={addingMember}
                                  />
                                </Field>

                                <Field label="Rol" htmlFor={`member-role-${tenant.id}`}>
                                  <select
                                    id={`member-role-${tenant.id}`}
                                    className="select"
                                    value={memberRole}
                                    onChange={(event) =>
                                      setMemberRole(event.target.value as MemberRole)
                                    }
                                    disabled={addingMember}
                                  >
                                    {MEMBER_ROLES.map((value) => (
                                      <option key={value} value={value}>
                                        {MEMBER_ROLE_LABELS[value]}
                                      </option>
                                    ))}
                                  </select>
                                </Field>
                              </div>

                              <Field
                                label="Contrasena"
                                htmlFor={`member-password-${tenant.id}`}
                                required
                                hint="Minimo 8 caracteres. Si la cuenta ya existe, no se cambia."
                              >
                                <input
                                  id={`member-password-${tenant.id}`}
                                  className="input"
                                  type="password"
                                  maxLength={200}
                                  value={memberPassword}
                                  onChange={(event) => setMemberPassword(event.target.value)}
                                  disabled={addingMember}
                                />
                              </Field>

                              {memberError ? (
                                <div className="banner banner--error" role="alert">
                                  <Icon name="error" className="banner__icon" />
                                  <p className="banner__text">{memberError}</p>
                                </div>
                              ) : null}

                              <div className="page__actions page__actions--end">
                                <button
                                  type="submit"
                                  className="button button--primary"
                                  disabled={addingMember}
                                >
                                  {addingMember ? (
                                    <Spinner label="Anadiendo…" />
                                  ) : (
                                    <><Icon name="plus" />Anadir persona</>
                                  )}
                                </button>
                              </div>
                            </form>
                          </section>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
